import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../utils/api';
import { PdfPreview } from '../PdfPreview';
import Pagination, { getPageSize } from '../Pagination';
import { useInstallOffer, takeInstallHow } from '../../utils/installApp';
import { useAppUpdate, AppUpdateBar } from '../../utils/appUpdate';
import { useStore } from '../../store';
import { DOC_GROUP } from '../../tabPalette';

// ── Files: the documents as an app a phone installs (report ac3c43b4) ────────────────────────────────────────────
// Calem: "I need you to make a phone-friendly version of the filing page so that users can access documents on the
// go", and, asked what it should do: "Only find and open documents. For staff only. Make it a downloadable app like
// you did with chat."
// So this is the File Library read on a phone, at an address of its own (/files), dressed as an app the way Chat is
// (components/Chat/ChatApp.tsx): one column, the same three sections as the Staff Dashboard's library
// (StaffFileLibrary — Public, Legal clients, Accounting clients), folders opened one at a time with a way back, a
// search box, and a document opened full-screen with Download and Share. Nothing here writes: no upload, rename,
// move or delete, no Drop Folder tools and no Rectify — those stay on the desktop Filing page, which is untouched.
// Every call is one the desktop library already makes (GET /api/files, /files/search, /files/clients and the
// file's own /download), and each section is behind the permission of the desktop page that shows it — the page itself
// behind the Filing page's (doc_storage), the two client sections behind the Staff Dashboard's — so a login sees here
// what it may already see there, and nothing more.
// What makes a phone offer to install it, as for Chat:
// · files.webmanifest (public/) — the name Files, its picture and the address /files in a window of its own.
//   index.html WRITES it into the head on a direct load of /files, before an iPhone looks; useFilesHead below sets it
//   for a walk in from another page and puts the site's own back on the way out.
// · sw.js (public/) — the caching-free service worker Chat registers over /chat, registered again over /files.
// · the Install app button in this page's top row (utils/installApp.ts): the browser's own dialog where it gave
//   the page one, else the few words that say which of the phone's own buttons to press.
const FILES_NAME = 'Files';                       // what the icon is called — no firm's name, as every tenant installs it
const FILES_MANIFEST = '/files.webmanifest';
// The picture under that name (report e32cc9f4): the chat's tile — the shield smaller inside a ring — with the ring in
// the Filing page's brown, drawn by the server (server/src/routes/chatIcon.ts). files.webmanifest names 192 and 512;
// this is the one an iPhone reads. index.html writes the same address on a direct load; a new ?v= belongs in both.
const FILES_ICON = '/api/files-icon/180.png?v=1';
const FILES_SCOPE = '/files';
// The screen's very edges, so env(safe-area-inset-*) keeps clear of an iPhone's notch and home bar — but the pinch
// zoom stays, unlike the chat's: a document is read here. index.html writes the very same line on a direct load.
const FILES_VIEWPORT = 'width=device-width, initial-scale=1, viewport-fit=cover';

type Node = {
  id: string; parent_id: string; is_folder: boolean; name: string; def_key?: string; filename?: string; mime?: string;
  size?: number; path?: string; system?: boolean;
};
type Crumb = { id: string; name: string; def_key?: string };
type FirmClient = { id: string; name: string };
type Section = 'personal' | 'legal' | 'accounting';

// The desktop library's own tests (FileLibrary/index.tsx: extOf, iconOf, canPreview, fmtSize): a PDF or a picture is
// drawn, anything else is offered to download or share.
const extOf = (n: Node) => (n.filename || n.name || '').split('.').pop()!.toLowerCase();
const iconOf = (n: Node) => {
  if (n.is_folder) return n.system ? 'folder-cog' : 'folder';
  const m = (n.mime || '').toLowerCase(), e = extOf(n);
  if (m.includes('pdf') || e === 'pdf') return 'file-type-pdf';
  if (m.startsWith('image') || ['png', 'jpg', 'jpeg', 'gif', 'webp', 'heic'].includes(e)) return 'photo';
  if (['xls', 'xlsx', 'csv'].includes(e)) return 'file-spreadsheet';
  if (['doc', 'docx'].includes(e)) return 'file-text';
  if (['zip', 'rar', '7z'].includes(e)) return 'file-zip';
  return 'file';
};
const canPreview = (n: Node) => { const m = (n.mime || '').toLowerCase(), e = extOf(n); return m.includes('pdf') || m.startsWith('image') || ['pdf', 'png', 'jpg', 'jpeg', 'gif', 'webp'].includes(e); };
const isPdf = (n: Node) => (n.mime || '').toLowerCase().includes('pdf') || extOf(n) === 'pdf';
const fmtSize = (n?: number) => { if (!n) return ''; if (n < 1024) return `${n} B`; if (n < 1048576) return `${(n / 1024).toFixed(1)} KB`; return `${(n / 1048576).toFixed(1)} MB`; };
const FOLDER_COLOR = '#E8A33D';   // the library's folder yellow (StaffFileLibrary, MovePicker)

/** The page's head while Files is the page: its own manifest, its own picture, its own name for an iPhone icon and its
 *  own viewport, all four put back as they were on the way out, so every other page installs as the portal and zooms
 *  as it always has. On a direct load of /files index.html has written them already; this is the walk in from another page. */
function useFilesHead(): void {
  useEffect(() => {
    // data-site-href / data-site-content carry the SITE's own, which index.html writes on every page — so the way out
    // restores the site's even after a direct load of /files.
    const link = document.head.querySelector<HTMLLinkElement>('link[rel="manifest"]');
    const was = link?.dataset.siteHref || link?.getAttribute('href') || '';
    link?.setAttribute('href', FILES_MANIFEST);
    const touch = document.head.querySelector<HTMLLinkElement>('link[rel="apple-touch-icon"]');
    const wasTouch = touch?.dataset.siteHref || touch?.getAttribute('href') || '';
    touch?.setAttribute('href', FILES_ICON);
    const already = document.head.querySelector<HTMLMetaElement>('meta[name="apple-mobile-web-app-title"]');
    const named = already || document.createElement('meta');
    named.name = 'apple-mobile-web-app-title';
    named.content = FILES_NAME;
    if (!already) document.head.appendChild(named);
    const view = document.head.querySelector<HTMLMetaElement>('meta[name="viewport"]');
    const wasView = view?.dataset.siteContent || view?.getAttribute('content') || '';
    view?.setAttribute('content', FILES_VIEWPORT);
    // Stores nothing (see sw.js), so an installed Files is never served a page older than the last deploy. A browser
    // with no service workers simply has none, and the page works the same — only the Install offer is missing.
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.register('/sw.js', { scope: FILES_SCOPE }).catch(() => { /* not installable, still usable */ });
    }
    return () => {
      if (link && was) link.setAttribute('href', was);
      if (touch && wasTouch) touch.setAttribute('href', wasTouch);
      if (view && wasView) view.setAttribute('content', wasView);
      named.remove();
    };
  }, []);
}

export default function FilesApp() {
  const { t } = useTranslation();
  useFilesHead();
  // "Install app", gone once Files IS the installed app; and the sheet that says what to tap, opened straight away
  // when Chat's Files button sent this phone here for it (installApp: wantInstallHow('files')).
  const offer = useInstallOffer();
  const [howTo, setHowTo] = useState(false);
  useEffect(() => { if (takeInstallHow('files')) setHowTo(true); }, []);
  const update = useAppUpdate();

  // The Legal / Accounting client folders are the Staff Dashboard's on the desktop (StaffFileLibrary), so they are
  // offered here only to whoever holds that module; the Public pool is the Filing page's, which /files sits behind.
  const firms = useStore(s => !!s.me?.modules.includes('staff_dashboard'));
  const [section, setSection] = useState<Section>('personal');
  const [clients, setClients] = useState<FirmClient[]>([]);
  const [client, setClient] = useState<FirmClient | null>(null);   // the Legal / Accounting client being read
  const [parent, setParent] = useState('');                        // the folder open, '' = the library's top
  const [crumbs, setCrumbs] = useState<Crumb[]>([]);               // the server's path down to it, itself last
  const [nodes, setNodes] = useState<Node[]>([]);
  // Two lists that can be on their way at once — a firm's clients and a folder — so each keeps its own state: a
  // folder given up half-loaded must not leave the client list spinning.
  const [clientsBusy, setClientsBusy] = useState(false);
  const [clientsFailed, setClientsFailed] = useState(false);
  const [folderBusy, setFolderBusy] = useState(false);
  const [folderFailed, setFolderFailed] = useState(false);
  const [q, setQ] = useState('');
  const [hits, setHits] = useState<Node[] | null>(null);           // null = no search running
  const [searching, setSearching] = useState(false);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(getPageSize);
  const [open, setOpen] = useState<Node | null>(null);             // the document shown full-screen
  const asked = useRef(0);                                         // the latest search; an older answer is dropped

  // The library being read: the shared pool, or one client's — none while a firm's client list is showing.
  const scope = section === 'personal' ? 'personal' : client ? `client:${client.id}` : '';
  const dispName = (n: { def_key?: string; name: string }) => (n.def_key ? t(n.def_key) : n.name);

  // Every move starts the list from its first page, with the search box empty.
  const go = (to: string) => { setParent(to); setQ(''); setHits(null); setPage(1); };
  const pickSection = (s: Section) => { setSection(s); setClient(null); go(''); };
  const pickClient = (c: FirmClient | null) => { setClient(c); setNodes([]); setCrumbs([]); go(''); };

  // A firm's clients, as the Staff Dashboard's library lists them.
  useEffect(() => {
    if (section === 'personal') { setClients([]); setClientsBusy(false); return; }
    let live = true;
    setClientsBusy(true); setClientsFailed(false);
    api.filesClients(section)
      .then((d: any) => { if (live) setClients(d.clients || []); })
      .catch(() => { if (live) { setClients([]); setClientsFailed(true); } })
      .finally(() => { if (live) setClientsBusy(false); });
    return () => { live = false; };
  }, [section]);

  // The open folder. open=1 when it is a folder walked into, which is what the desktop sends too (Quick Access).
  useEffect(() => {
    if (!scope) { setFolderBusy(false); return; }
    let live = true;
    setFolderBusy(true); setFolderFailed(false);
    api.filesList(scope, parent, !!parent)
      .then((d: any) => { if (live) { setNodes(d.nodes || []); setCrumbs(d.breadcrumb || []); } })
      .catch(() => { if (live) { setNodes([]); setFolderFailed(true); } })
      .finally(() => { if (live) setFolderBusy(false); });
    return () => { live = false; };
  }, [scope, parent]);

  // The search box, 300ms after the last key: the open folder and everything under it, as on the desktop. With no
  // library open (a firm's client list) it only narrows that list, below, and asks the server nothing.
  useEffect(() => {
    const mine = ++asked.current;   // any change supersedes an answer still on its way
    const s = q.trim();
    if (!scope || !s) { setHits(null); setSearching(false); return; }
    const timer = window.setTimeout(() => {
      setSearching(true);
      api.filesSearch(scope, s, parent, false, true)   // the Inactive folders too: this page has no tick to leave them out
        .then((d: any) => { if (asked.current === mine) setHits(d.nodes || []); })
        .catch(() => { if (asked.current === mine) setHits([]); })
        .finally(() => { if (asked.current === mine) setSearching(false); });
    }, 300);
    return () => window.clearTimeout(timer);
  }, [q, scope, parent]);

  const back = () => {
    if (parent) go(crumbs.length > 1 ? crumbs[crumbs.length - 2].id : '');
    else if (client) pickClient(null);
  };
  const canBack = !!parent || !!client;
  const sectionLabel = t(section === 'personal' ? 'fl_personal' : section === 'legal' ? 'fl_legal' : 'fl_accounting');
  const title = parent && crumbs.length ? dispName(crumbs[crumbs.length - 1]) : client ? client.name : t('files_title');

  // What the list shows: a firm's clients (narrowed by the box), the search's hits, or the open folder.
  const clientList = !scope;
  const loading = clientList ? clientsBusy : folderBusy;
  const failed = clientList ? clientsFailed : folderFailed;
  const needle = q.trim().toLowerCase();
  const shownClients = clientList ? clients.filter(c => !needle || c.name.toLowerCase().includes(needle)) : [];
  const rows: Node[] = clientList ? [] : hits ?? nodes;
  // An open folder is two lists (Calem, report 239aaf49: "make the folders at the top of the list their own separate
  // bubble and fix them in this location (i.e. do not scroll)"): its sub-folders, pinned at the top of the scroll box in
  // a bubble of their own, and its documents, paged, in the bubble under them — the server sends the folders first, so
  // this only parts the two. A search's hits and a firm's client list stay one list each, scrolling as they did.
  const folderView = !clientList && !hits;
  const folders = folderView ? rows.filter(n => n.is_folder) : [];
  const paged = folderView ? rows.filter(n => !n.is_folder) : rows;
  const total = clientList ? shownClients.length : paged.length;
  const from = (page - 1) * limit;

  const tab = (k: Section, icon: string, label: string) => (
    <button key={k} onClick={() => pickSection(k)} aria-pressed={section === k}
      style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '7px 12px', borderRadius: 999, border: '1px solid', fontSize: 13, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0, borderColor: section === k ? 'var(--accent)' : 'var(--border-strong)', background: section === k ? 'var(--accent)' : 'transparent', color: section === k ? '#fff' : 'var(--text-secondary)' }}>
      <i className={`ti ti-${icon}`} aria-hidden />{label}
    </button>
  );
  const row = (key: string, icon: string, color: string, label: string, sub: string, onClick: () => void, folder: boolean, first: boolean) => (
    <button key={key} onClick={onClick}
      style={{ display: 'flex', alignItems: 'center', gap: 12, width: '100%', textAlign: 'left', padding: '11px 14px', border: 'none', borderTop: first ? 'none' : '1px solid var(--border)', background: 'var(--bg-surface)', cursor: 'pointer' }}>
      <i className={`ti ti-${icon}`} style={{ fontSize: 22, color, flexShrink: 0 }} aria-hidden />
      <span style={{ flex: 1, minWidth: 0 }}>
        {/* The whole name, wrapped: a document's name is how it is told apart (Type (Person) Company date). */}
        <span style={{ display: 'block', fontSize: 14.5, fontWeight: 600, color: 'var(--text-primary)', overflowWrap: 'anywhere' }}>{label}</span>
        {!!sub && <span style={{ display: 'block', marginTop: 2, fontSize: 12, color: 'var(--text-tertiary)', overflowWrap: 'anywhere' }}>{sub}</span>}
      </span>
      {folder && <i className="ti ti-chevron-right" style={{ fontSize: 16, color: 'var(--text-tertiary)', flexShrink: 0 }} aria-hidden />}
    </button>
  );
  const note = (text: string) => <div style={{ textAlign: 'center', padding: '40px 16px', color: 'var(--text-tertiary)', fontSize: 13.5 }}>{text}</div>;
  const spinner = <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-tertiary)' }}><i className="ti ti-loader" style={{ fontSize: 22, display: 'inline-block', animation: 'spin 1s linear infinite' }} aria-hidden /></div>;

  const bubble: React.CSSProperties = { border: '1px solid var(--border)', borderRadius: 10, overflow: 'hidden', background: 'var(--bg-surface)' };
  const item = (n: Node, i: number) => row(n.id, iconOf(n), n.is_folder ? FOLDER_COLOR : 'var(--text-secondary)', dispName(n),
    hits ? (n.path || '') : n.is_folder ? '' : fmtSize(n.size),
    () => (n.is_folder ? go(n.id) : setOpen(n)), n.is_folder, i === 0);

  let list: React.ReactNode;
  if (loading || (searching && hits === null)) list = <div style={bubble}>{spinner}</div>;
  else if (failed) list = <div style={bubble}>{note(t('files_load_fail'))}</div>;
  else if (clientList) {
    list = <div style={bubble}>{shownClients.length === 0 ? note(t('fl_no_clients'))
      : shownClients.slice(from, from + limit).map((c, i) => row(c.id, 'folder', FOLDER_COLOR, c.name, '', () => pickClient(c), true, i === 0))}</div>;
  } else if (rows.length === 0) list = <div style={bubble}>{note(t(hits ? 'fl_no_match' : 'fl_empty_folder'))}</div>;
  else {
    // The folders' bubble is pinned: sticky at the top of the scroll box (the one under the header), on the page's own
    // colour so the documents going under it don't show through. The 10px it reaches up over the search box's gap it
    // gives back as padding, so the bubble stands where it did, and its 10px under are the gap to the documents' bubble
    // (which, with no sub-folder, is the list on its own, where it was). At most 45vh tall, scrolling inside, so a folder
    // full of sub-folders can't keep its documents off the screen. Only the documents are paged.
    list = (
      <>
        {folders.length > 0 && (
          <div style={{ position: 'sticky', top: 0, zIndex: 1, background: 'var(--bg-app)', marginTop: -10, padding: '10px 0' }}>
            <div style={{ ...bubble, maxHeight: '45vh', overflowY: 'auto', WebkitOverflowScrolling: 'touch' } as React.CSSProperties}>{folders.map(item)}</div>
          </div>
        )}
        {paged.length > 0 && <div style={bubble}>{paged.slice(from, from + limit).map(item)}</div>}
      </>
    );
  }

  return (
    // The whole screen to its edges (viewport-fit=cover), so the page keeps itself clear of a notch and a home bar.
    <div style={{ position: 'fixed', inset: 0, display: 'flex', flexDirection: 'column', background: 'var(--bg-app)', padding: 'env(safe-area-inset-top) env(safe-area-inset-right) 0 env(safe-area-inset-left)' }}>
      <style>{'@keyframes spin { from { transform: rotate(0deg);} to { transform: rotate(360deg);} }'}</style>
      {update && <AppUpdateBar />}
      <header style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', borderBottom: '1px solid var(--border)', background: 'var(--bg-surface)', flexShrink: 0 }}>
        {canBack ? (
          <button onClick={back} title={t('files_back')} aria-label={t('files_back')}
            style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 32, height: 32, padding: 0, borderRadius: 8, border: 'none', background: 'transparent', color: 'var(--text-primary)', cursor: 'pointer', flexShrink: 0 }}>
            <i className="ti ti-arrow-left" style={{ fontSize: 20 }} aria-hidden />
          </button>
        ) : (
          <i className="ti ti-folders" style={{ fontSize: 21, color: 'var(--accent)', flexShrink: 0 }} aria-hidden />
        )}
        <div style={{ flex: 1, minWidth: 0, fontSize: 16, fontWeight: 800, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{title}</div>
        {/* In the top menu's Document colours, as the app's ring is (report 89771ce1). */}
        {offer.show && (
          <button onClick={() => { offer.install().then(opened => { if (!opened) setHowTo(true); }); }}
            title={t('files_install_tip')} aria-label={t('chat_install')}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '5px 9px', borderRadius: 'var(--radius-md)', fontSize: 12, fontWeight: 600, cursor: 'pointer', flexShrink: 0, color: DOC_GROUP.color, background: DOC_GROUP.tint, border: `1px solid ${DOC_GROUP.color}` }}>
            <i className="ti ti-download" style={{ fontSize: 14 }} aria-hidden />{t('chat_install')}
          </button>
        )}
      </header>

      <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', WebkitOverflowScrolling: 'touch' } as React.CSSProperties}>
        <div style={{ width: '100%', maxWidth: 720, margin: '0 auto', padding: '12px 12px calc(16px + env(safe-area-inset-bottom))', boxSizing: 'border-box' }}>
          {firms && (
            <div style={{ display: 'flex', gap: 8, overflowX: 'auto', paddingBottom: 2, marginBottom: 10 }}>
              {tab('personal', 'users', t('fl_personal'))}
              {tab('legal', 'scale', t('fl_legal'))}
              {tab('accounting', 'calculator', t('fl_accounting'))}
            </div>
          )}

          {/* Where this folder is, each step a way back up to it. */}
          {!!parent && (
            <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 2, marginBottom: 10, fontSize: 12.5, color: 'var(--text-tertiary)' }}>
              <button onClick={() => go('')} style={{ border: 'none', background: 'none', padding: '2px 3px', color: 'var(--accent)', fontSize: 12.5, cursor: 'pointer' }}>{client ? client.name : sectionLabel}</button>
              {crumbs.map((c, i) => (
                <React.Fragment key={c.id}>
                  <i className="ti ti-chevron-right" style={{ fontSize: 11 }} aria-hidden />
                  {i === crumbs.length - 1
                    ? <span style={{ padding: '2px 3px', color: 'var(--text-secondary)', fontWeight: 600 }}>{dispName(c)}</span>
                    : <button onClick={() => go(c.id)} style={{ border: 'none', background: 'none', padding: '2px 3px', color: 'var(--accent)', fontSize: 12.5, cursor: 'pointer' }}>{dispName(c)}</button>}
                </React.Fragment>
              ))}
            </div>
          )}

          {/* 16px type: an iPhone zooms the page in on any box typed into at less. */}
          <div style={{ position: 'relative', marginBottom: 10 }}>
            <i className="ti ti-search" style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', fontSize: 16, color: 'var(--text-tertiary)' }} aria-hidden />
            <input value={q} onChange={e => { setQ(e.target.value); setPage(1); }} type="text" enterKeyHint="search"
              placeholder={t(clientList ? 'fl_search_client' : 'files_search_ph')}
              style={{ width: '100%', padding: '10px 38px 10px 38px', fontSize: 16, border: '1px solid var(--border-strong)', borderRadius: 10, background: 'var(--bg-surface)', color: 'var(--text-primary)', outline: 'none', boxSizing: 'border-box' }} />
            {!!q && (
              <button onClick={() => { setQ(''); setPage(1); }} title={t('fl_preview_close')} aria-label={t('fl_preview_close')}
                style={{ position: 'absolute', right: 6, top: '50%', transform: 'translateY(-50%)', display: 'flex', alignItems: 'center', justifyContent: 'center', width: 28, height: 28, padding: 0, border: 'none', borderRadius: '50%', background: 'transparent', color: 'var(--text-tertiary)', cursor: 'pointer' }}>
                <i className="ti ti-x" style={{ fontSize: 16 }} aria-hidden />
              </button>
            )}
          </div>

          {hits && !searching && <div style={{ fontSize: 12, color: 'var(--text-tertiary)', marginBottom: 6 }}>{t('fl_search_results')} · {hits.length}</div>}
          {clientList && !loading && !failed && shownClients.length > 0 && <div style={{ fontSize: 12, color: 'var(--text-tertiary)', marginBottom: 6 }}>{shownClients.length} {t('fl_clients_count')}</div>}
          {list}
          {/* At most a page of rows at a time, as every list in the system shows them (the server sends a folder whole). */}
          <div style={{ marginTop: 10 }}>
            <Pagination page={page} total={total} limit={limit} onPage={setPage} onLimit={n => { setLimit(n); setPage(1); }} />
          </div>
        </div>
      </div>

      {open && <FileSheet node={open} onClose={() => setOpen(null)} />}
      {howTo && <InstallHow onClose={() => setHowTo(false)} />}
    </div>
  );
}

/** What navigator.share / canShare take here — a file and its name. Typed here, as not every TypeScript DOM library
 *  carries the files a share may hold. */
type SharePayload = { files: File[]; title?: string };

/** One document, full-screen: drawn as the desktop draws it (PdfPreview for a PDF, the picture itself for a photo),
 *  with Download and Share. Share hands the file to the phone's own share sheet (WhatsApp, Mail, Save to Files…) and
 *  falls back to a download where the phone cannot share files. The file is fetched once, as the sheet opens, so both
 *  buttons answer straight from the press — a phone refuses a share that waited on the network first. */
function FileSheet({ node, onClose }: { node: Node; onClose: () => void }) {
  const { t } = useTranslation();
  const [url, setUrl] = useState('');
  const [blob, setBlob] = useState<Blob | null>(null);
  const [failed, setFailed] = useState(false);
  const fileName = node.filename || node.name || 'file';
  const nav = navigator as unknown as { share?: (d: SharePayload) => Promise<void>; canShare?: (d: SharePayload) => boolean };

  useEffect(() => {
    let live = true, made = '';
    api.filesBlob(node.id)
      .then(async u => {
        if (!live) { URL.revokeObjectURL(u); return; }
        made = u; setUrl(u);
        try { const b = await (await fetch(u)).blob(); if (live) setBlob(b); } catch { if (live) setFailed(true); }
      })
      .catch(() => { if (live) setFailed(true); });
    return () => { live = false; if (made) URL.revokeObjectURL(made); };
  }, [node.id]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const download = () => {
    if (!url) return;
    const a = document.createElement('a'); a.href = url; a.download = fileName;
    document.body.appendChild(a); a.click(); a.remove();
  };
  const share = async () => {
    if (!blob || !nav.share) return;
    // A file the server would not show inline comes back untyped; its stored type is the better name for it.
    const type = blob.type && blob.type !== 'application/octet-stream' ? blob.type : (node.mime || blob.type || 'application/octet-stream');
    const data: SharePayload = { files: [new File([blob], fileName, { type })], title: node.name };
    if (nav.canShare && !nav.canShare(data)) { download(); return; }
    try { await nav.share(data); }
    catch (e: any) { if (e?.name !== 'AbortError') download(); }   // AbortError = the sheet was closed on purpose
  };

  const body = failed ? (
    <div style={{ textAlign: 'center', padding: '40px 16px', color: 'var(--text-tertiary)', fontSize: 13.5 }}>
      <i className="ti ti-file-alert" style={{ fontSize: 28, display: 'block', marginBottom: 8 }} aria-hidden />{t('files_open_fail')}
    </div>
  ) : !url ? (
    <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-tertiary)' }}><i className="ti ti-loader" style={{ fontSize: 24, display: 'inline-block', animation: 'spin 1s linear infinite' }} aria-hidden /></div>
  ) : canPreview(node) ? (
    isPdf(node) ? <PdfPreview src={url} /> : <img src={url} alt="" style={{ width: '100%', display: 'block', borderRadius: 6, background: '#fff' }} />
  ) : (
    <div style={{ textAlign: 'center', padding: '40px 16px', color: 'var(--text-secondary)', fontSize: 13.5 }}>
      <i className={`ti ti-${iconOf(node)}`} style={{ fontSize: 44, display: 'block', marginBottom: 10, color: 'var(--text-tertiary)' }} aria-hidden />
      {!!node.size && <div style={{ marginBottom: 6, fontSize: 12.5, color: 'var(--text-tertiary)' }}>{fmtSize(node.size)}</div>}
      {t('files_no_preview')}
    </div>
  );
  const ready = !!url && !failed;
  const bigBtn = (primary: boolean, off: boolean): React.CSSProperties => ({
    flex: 1, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 7, padding: '12px 10px', borderRadius: 10,
    fontSize: 14.5, fontWeight: 700, cursor: off ? 'default' : 'pointer', opacity: off ? 0.5 : 1,
    border: primary ? 'none' : '1px solid var(--border-strong)', background: primary ? 'var(--accent)' : 'var(--bg-surface)', color: primary ? '#fff' : 'var(--text-primary)',
  });

  return (
    <div role="dialog" aria-modal="true" aria-label={node.name}
      style={{ position: 'fixed', inset: 0, zIndex: 1500, display: 'flex', flexDirection: 'column', background: 'var(--bg-app)', padding: 'env(safe-area-inset-top) env(safe-area-inset-right) 0 env(safe-area-inset-left)' }}>
      <header style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', borderBottom: '1px solid var(--border)', background: 'var(--bg-surface)', flexShrink: 0 }}>
        <button onClick={onClose} title={t('fl_preview_close')} aria-label={t('fl_preview_close')}
          style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 32, height: 32, padding: 0, borderRadius: 8, border: 'none', background: 'transparent', color: 'var(--text-primary)', cursor: 'pointer', flexShrink: 0 }}>
          <i className="ti ti-arrow-left" style={{ fontSize: 20 }} aria-hidden />
        </button>
        <div style={{ flex: 1, minWidth: 0, fontSize: 14, fontWeight: 700, color: 'var(--text-primary)', overflowWrap: 'anywhere', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' } as React.CSSProperties}>{node.name}</div>
      </header>
      <div style={{ flex: 1, minHeight: 0, overflow: 'auto', background: 'var(--c-gray-100)', padding: 10 }}>
        <div style={{ width: '100%', maxWidth: 1000, margin: '0 auto' }}>{body}</div>
      </div>
      <div style={{ display: 'flex', gap: 10, padding: '10px 12px calc(10px + env(safe-area-inset-bottom))', borderTop: '1px solid var(--border)', background: 'var(--bg-surface)', flexShrink: 0 }}>
        <button onClick={download} disabled={!ready} style={bigBtn(!nav.share, !ready)}>
          <i className="ti ti-download" style={{ fontSize: 18 }} aria-hidden />{t('fl_download')}
        </button>
        {!!nav.share && (
          <button onClick={() => { void share(); }} disabled={!blob} style={bigBtn(true, !blob)}>
            <i className="ti ti-share" style={{ fontSize: 18 }} aria-hidden />{t('files_share')}
          </button>
        )}
      </div>
    </div>
  );
}

/** How to install, for a phone whose browser offers no dialog of its own — Chat's sheet (ChatPanel: InstallHow) with
 *  the Files app's own words where Chat's name Chat. Both phones are always shown, rather than guessed at. */
function InstallHow({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation();
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  const step: React.CSSProperties = { padding: '10px 12px', borderRadius: 10, border: '1px solid var(--border-strong)', background: 'var(--bg-app)', fontSize: 12.5, color: 'var(--text-primary)' };
  const head: React.CSSProperties = { marginBottom: 3, fontSize: 11.5, fontWeight: 700, color: 'var(--text-tertiary)' };
  return (
    <div onMouseDown={e => { if (e.target === e.currentTarget) onClose(); }}
      role="dialog" aria-modal="true" aria-label={t('files_install_how_title')}
      style={{ position: 'fixed', inset: 0, zIndex: 1600, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 18 }}>
      <div style={{ width: 420, maxWidth: '100%', background: 'var(--bg-surface)', borderRadius: 14, boxShadow: 'var(--shadow-md)', overflow: 'hidden' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 9, padding: '12px 14px', borderBottom: '1px solid var(--border-strong)' }}>
          <i className="ti ti-download" style={{ fontSize: 19, color: 'var(--accent)' }} aria-hidden />
          <div style={{ flex: 1, minWidth: 0, fontSize: 15, fontWeight: 800, color: 'var(--text-primary)' }}>{t('files_install_how_title')}</div>
          <button onClick={onClose} title={t('chat_install_close')} aria-label={t('chat_install_close')}
            style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 28, height: 28, padding: 0, borderRadius: 8, border: 'none', background: 'transparent', color: 'var(--text-tertiary)', cursor: 'pointer' }}>
            <i className="ti ti-x" style={{ fontSize: 16 }} aria-hidden />
          </button>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, padding: 14 }}>
          <div style={{ fontSize: 12.5, color: 'var(--text-secondary)' }}>{t('files_install_how_intro')}</div>
          <div style={step}><div style={head}>{t('chat_install_ios_head')}</div>{t('files_install_ios')}</div>
          <div style={step}><div style={head}>{t('chat_install_android_head')}</div>{t('chat_install_android')}</div>
        </div>
      </div>
    </div>
  );
}
